import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Building2, ArrowLeft, FileCheck, Package, Landmark } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const FixedAssetVerificationDelhi = () => {
  const handleCall = () => window.location.href = 'tel:9205514887';
  const handleWhatsApp = () => window.open('https://wa.me/919205514887', '_blank');

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Fixed Asset Verification in Delhi | Asset Audit Services – AudiX</title>
        <meta name="description" content="Professional fixed asset verification services in Delhi. AudiX provides physical verification, asset tagging, and reconciliation services for businesses in Delhi NCR." />
        <link rel="canonical" href="https://www.audix.co.in/fixed-asset-verification-delhi" />
      </Helmet>

      {/* Header */}
      <header className="bg-white shadow-md py-4 px-4 sticky top-0 z-50">
        <div className="container mx-auto max-w-6xl flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-700 hover:text-blue-600">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
          <Button onClick={handleCall} size="sm" className="bg-blue-600 hover:bg-blue-700">
            <Phone className="h-4 w-4 mr-2" /> Call Us
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white py-20 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Fixed Asset Verification in Delhi</h1>
          <p className="text-xl text-slate-200 max-w-3xl mx-auto mb-8">
            Professional fixed asset verification and reconciliation services ensuring accuracy of asset records and compliance with accounting standards.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={handleCall} size="lg" className="bg-blue-600 hover:bg-blue-700">
              <Phone className="h-5 w-5 mr-2" /> Get Quote
            </Button>
            <Button onClick={handleWhatsApp} size="lg" className="bg-green-600 hover:bg-green-700">
              <MessageCircle className="h-5 w-5 mr-2" /> WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Fixed Asset Services We Provide</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <Building2 className="h-10 w-10 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold mb-2">Physical Verification</h3>
                <p className="text-slate-600">Complete physical verification of all fixed assets with detailed condition assessment.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <FileCheck className="h-10 w-10 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold mb-2">Asset Tagging</h3>
                <p className="text-slate-600">Systematic asset tagging and barcode implementation for better tracking.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <Landmark className="h-10 w-10 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold mb-2">Reconciliation</h3>
                <p className="text-slate-600">Reconciliation of physical assets with book records and variance analysis.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-slate-100">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Benefits of Fixed Asset Verification</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              'Accurate asset register maintenance',
              'Compliance with accounting standards',
              'Detection of ghost assets and losses',
              'Improved asset utilization tracking',
              'Support for financial reporting',
              'Insurance and valuation support'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <p className="text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Related Services</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/audit-firm-in-delhi" className="bg-blue-100 text-blue-800 px-6 py-3 rounded-lg font-medium hover:bg-blue-200">
              Audit Firm in Delhi
            </Link>
            <Link to="/stock-audit-services-delhi" className="bg-blue-100 text-blue-800 px-6 py-3 rounded-lg font-medium hover:bg-blue-200">
              Stock Audit Services
            </Link>
            <Link to="/internal-audit-services-delhi" className="bg-blue-100 text-blue-800 px-6 py-3 rounded-lg font-medium hover:bg-blue-200">
              Internal Audit Services
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-blue-900 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-4">Need Fixed Asset Verification?</h2>
          <p className="text-xl text-blue-100 mb-8">Contact us for professional asset verification and reconciliation services in Delhi.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={handleCall} size="lg" className="bg-white text-blue-900 hover:bg-slate-100">
              <Phone className="h-5 w-5 mr-2" /> 9205514887
            </Button>
            <Button onClick={handleWhatsApp} size="lg" className="bg-green-600 hover:bg-green-700">
              <MessageCircle className="h-5 w-5 mr-2" /> WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-8 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <p className="text-slate-400">© 2025 AudiX Solutions & Co. All rights reserved.</p>
          <Link to="/" className="text-blue-400 hover:text-blue-300 mt-2 inline-block">← Back to Homepage</Link>
        </div>
      </footer>
    </div>
  );
};

export default FixedAssetVerificationDelhi;
